import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState, useContext, forwardRef, useEffect } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import { Toggle } from 'remote-com-shared/components/HeaderNavigation/Toggle';
import { Navigation } from 'remote-com-shared/components/HeaderNavigation/styles/Navigation';
import HeroContext from 'remote-com-shared/components/Hero/context';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { useBreakpointRange } from 'remote-com-shared/style/breakpoints';
import { whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { RemoteLogo, Wrapper } from './styles';
import { BrandContainer, Brand } from './styles/Brand';

import { fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';

const WrapperPosition = styled.div`
  position: relative;

  ${whenProp(
    '$useRemoteFont',
    css`
      font-family: var(--typography-fontBrand);
    `
  )}
`;

const Header = forwardRef(function Header(props, ref) {
  const {
    compactDisplayCTA = true,
    brandHref = '/',
    brandSuffix,
    useRemoteFont = false,
    children,
  } = props;
  const { isIntersecting } = useContext(HeroContext);
  const [isOpen, setIsOpen] = useState(false);

  function closeNavigation() {
    setIsOpen(false);
  }

  const router = useRouter();

  useBreakpointRange(
    { to: fromDesktopBreakpoint },
    {
      onLeave: () => closeNavigation(),
    }
  );

  useEffect(() => {
    function handleHashChange(event) {
      const { target } = event;
      const { location: { hash: locationHash = '' } = {} } = target || {};
      if (locationHash.length > 0) {
        closeNavigation();
      }
    }

    function handleRouteChange() {
      closeNavigation();
    }

    window.addEventListener('hashchange', handleHashChange, true);
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange, true);
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  return (
    <>
      {useRemoteFont && (
        <Head>
          <link rel="stylesheet" href={staticAssetUrl('/fonts/import-remote-sans.css')} />
        </Head>
      )}
      <WrapperPosition $useRemoteFont={useRemoteFont}>
        <Wrapper $isOpen={isOpen} $isFixed={!isIntersecting} ref={ref} {...props}>
          <BrandContainer $isOpen={isOpen} $isFixed={!isIntersecting}>
            <Brand as="a" aria-label="Visit homepage" href={brandHref}>
              <RemoteLogo $isOpen={isOpen} $isFixed={!isIntersecting} />
              {brandSuffix && brandSuffix({ $isOpen: isOpen, $isFixed: !isIntersecting })}
            </Brand>

            {children && (
              <Toggle
                isOpen={isOpen}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Open navigation"
              >
                {compactDisplayCTA && (
                  <Button
                    tone="primary"
                    size="xs"
                    shadow
                    href={`${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`}
                    rel="nofollow"
                  >
                    Get started
                  </Button>
                )}
              </Toggle>
            )}
          </BrandContainer>

          {children && (
            <Navigation $isOpen={isOpen} aria-hidden={!isOpen}>
              {children}
            </Navigation>
          )}
        </Wrapper>
      </WrapperPosition>
    </>
  );
});

export default Header;
